/**
 * Paper Size Types and Constants
 * Standard paper sizes for invoice templates
 */

export type PaperPreset = 'A4' | 'A5' | 'Letter' | 'Legal' | 'Custom';

export type PaperOrientation = 'portrait' | 'landscape';

export interface PaperSize {
  preset: PaperPreset;
  width: number;  // width in mm
  height: number; // height in mm
  orientation: PaperOrientation;
}

/**
 * Standard paper dimensions in millimeters
 */
export const PAPER_PRESETS: Record<PaperPreset, { width: number; height: number }> = {
  A4: { width: 210, height: 297 },
  A5: { width: 148, height: 210 },
  Letter: { width: 216, height: 279 },
  Legal: { width: 216, height: 356 },
  Custom: { width: 210, height: 297 }, // Default to A4
};

/**
 * Default paper size
 */
export const DEFAULT_PAPER_SIZE: PaperSize = {
  preset: 'A4',
  width: PAPER_PRESETS.A4.width,
  height: PAPER_PRESETS.A4.height,
  orientation: 'portrait',
};

/**
 * Zoom preset levels
 */
export const ZOOM_PRESETS = {
  FIT_TO_WIDTH: 'fit-width',
  FIT_TO_PAGE: 'fit-page',
  25: 0.25,
  50: 0.5,
  75: 0.75,
  100: 1.0,
  125: 1.25,
  150: 1.5,
  200: 2.0,
  300: 3.0,
  400: 4.0,
} as const;

export type ZoomPreset = keyof typeof ZOOM_PRESETS | number;

/**
 * Convert paper size from mm to pixels (at 96 DPI)
 */
export function mmToPx(mm: number): number {
  return (mm * 96) / 25.4;
}

/**
 * Convert paper size from pixels to mm (at 96 DPI)
 */
export function pxToMm(px: number): number {
  return (px * 25.4) / 96;
}

/**
 * Get paper dimensions in pixels
 */
export function getPaperSizeInPx(paperSize: PaperSize): { width: number; height: number } {
  const isLandscape = paperSize.orientation === 'landscape';
  const width = isLandscape ? paperSize.height : paperSize.width;
  const height = isLandscape ? paperSize.width : paperSize.height;

  return {
    width: mmToPx(width),
    height: mmToPx(height),
  };
}

/**
 * Get paper dimensions in inches
 */
export function getPaperSizeInInches(paperSize: PaperSize): { width: number; height: number } {
  const isLandscape = paperSize.orientation === 'landscape';
  const width = isLandscape ? paperSize.height : paperSize.width;
  const height = isLandscape ? paperSize.width : paperSize.height;

  return {
    width: width / 25.4,
    height: height / 25.4,
  };
}

/**
 * Format paper size label for display
 */
export function formatPaperSizeLabel(paperSize: PaperSize): string {
  if (paperSize.preset === 'Custom') {
    return `${paperSize.width}×${paperSize.height}mm`;
  }
  return `${paperSize.preset} (${paperSize.orientation})`;
}

/**
 * Create a paper size object
 */
export function createPaperSize(
  preset: PaperPreset,
  orientation: PaperOrientation = 'portrait',
  customWidth?: number,
  customHeight?: number
): PaperSize {
  const dimensions = preset === 'Custom' && customWidth && customHeight
    ? { width: customWidth, height: customHeight }
    : PAPER_PRESETS[preset];

  return {
    preset,
    width: dimensions.width,
    height: dimensions.height,
    orientation,
  };
}
